import { type SbBlokData, storyblokEditable } from "@storyblok/react";
import useEmblaCarousel from "embla-carousel-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";

import Animated from "@/components/Animation/Animated";
import useDevice from "@/lib/hooks/useDevice";
import getLink from "@/lib/storyblok/getLink";
import ArrowSvg from "@/public/svg/Arrow.svg";

import type { ArticleCardBlok } from "../ArticleCard/ArticleCard";
import ArticleCard from "../ArticleCard/ArticleCard";
import type { StoryblokLinkBlok } from "../StoryblokLink/StoryblokLink";
import styles from "./curatedArticles.module.scss";

export interface CuratedArticlesProps {
  blok: CuratedArticlesBlok;
}

export interface CuratedArticlesBlok extends SbBlokData {
  title: string;
  link: StoryblokLinkBlok[];
  articles: {
    content: ArticleCardBlok;
    uuid: string;
    full_slug: string;
  }[];
}

export const CuratedArticles = ({ blok }: CuratedArticlesProps) => {
  const t = useTranslations();
  const { mobile } = useDevice();
  const [emblaRef, emblaApi] = useEmblaCarousel(
    mobile
      ? undefined
      : {
          dragFree: true,
          containScroll: "trimSnaps",
        }
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();

    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
    emblaApi.on("resize", onSelect);

    const timer = setTimeout(onSelect, 100);

    return () => {
      clearTimeout(timer);
      emblaApi.off("reInit", onSelect);
      emblaApi.off("select", onSelect);
      emblaApi.off("resize", onSelect);
    };
  }, [emblaApi, onSelect]);

  const link = blok.link?.[0];
  const showArrows = blok?.articles?.length > 3;

  return (
    <div
      className={styles.container}
      key={blok?._uid}
      {...storyblokEditable(blok)}
    >
      <Animated>
        {link ? (
          <Link href={getLink(link.link)} className={styles.title}>
            {blok.title}
          </Link>
        ) : (
          <h2 className={styles.title}>{blok.title}</h2>
        )}
      </Animated>

      <div className={styles.carouselWrapper}>
        <div
          className={styles.emblaViewport}
          ref={mobile ? undefined : emblaRef}
        >
          <div className={styles.wrapper}>
            {blok?.articles?.map((article, i) => (
              <div className={styles.articleCard} key={i + "-article"}>
                <ArticleCard story={article} index={i} animated={false} />
              </div>
            ))}
          </div>
        </div>

        {showArrows && (
          <div className={styles.navigationContainer}>
            <button
              type="button"
              className={`${styles.navButton} ${
                !canScrollPrev ? styles.disabled : ""
              }`}
              disabled={!canScrollPrev}
              onClick={scrollPrev}
              aria-label="Previous articles"
            >
              <ArrowSvg />
              {t("Previous")}
            </button>
            <button
              type="button"
              className={`${styles.navButton} ${
                !canScrollNext ? styles.disabled : ""
              }`}
              disabled={!canScrollNext}
              onClick={scrollNext}
              aria-label="Next articles"
            >
              {t("Next")}
              <ArrowSvg />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
